<template>
  <div>
    <!-- Filter bar (hidden in compact mode) -->
    <div v-if="!compact" class="d-flex field-filters flex-wrap mb-2 sticky-top glass-pane py-2 filter-bar-edge">
      <b-badge
        :variant="selectedTypes.length === defaultTypes.length ? 'primary' : 'secondary'"
        @click="clearFilters"
      >
        All Activity
      </b-badge>
      <b-badge
        v-for="type in availableTypes"
        :key="type.value"
        :variant="selectedTypes.includes(type.value) ? 'primary' : 'secondary'"
        @click="toggleFilterTag(type.value)"
      >
        <i :class="type.icon" class="mr-1"></i>
        {{ type.label }}
      </b-badge>
    </div>

    <!-- Partial-failure warning -->
    <b-alert v-if="failedProviders.length > 0" show variant="warning" class="py-1 px-2 small mb-2">
      <i class="fa fa-exclamation-triangle mr-1"></i>
      Some activity types couldn't be loaded: {{ failedProviders.join(', ') }}. Results may be incomplete.
    </b-alert>

    <!-- Loading state -->
    <div v-if="loading && items.length === 0" class="text-center p-3">
      <i class="fa fa-spinner fa-spin"></i>
      Loading activity...
    </div>

    <!-- Error state -->
    <div v-else-if="error" class="text-center text-danger p-3">
      <i class="fa fa-exclamation-triangle"></i>
      {{ error }}
    </div>

    <!-- Empty state -->
    <div v-else-if="items.length === 0" class="text-center text-muted p-3">
      No activity found.
    </div>

    <!-- Activity list -->
    <div v-else>
      <table class="activity-table" :class="{ 'edge-to-edge': !compact }">
        <template v-for="group in groupedItems">
          <tr :key="'hdr_' + group.label" class="time-group-header">
            <td :colspan="compact ? 2 : 3" class="text-muted small font-weight-bold text-uppercase px-1 pt-2 pb-1">
              {{ group.label }}
            </td>
          </tr>
          <tr
            v-for="item in group.items"
            :key="item.activityId"
            class="activity-row"
          >
            <td v-if="!compact" class="col-type">
              <span class="activity-type-label">
                <i :class="item.icon || 'fas fa-circle'" class="mr-1"></i>{{ getTypeLabel(item.activityType) }}
              </span>
            </td>
            <td class="col-summary">
              <div v-if="compact" class="activity-type-label" style="margin-bottom: 2px;">
                <i :class="item.icon || 'fas fa-circle'" class="mr-1"></i>{{ getTypeLabel(item.activityType) }}
              </div>
              <router-link
                v-if="item.contextLabel && getRouteForLink(item.linkType, item.linkId)"
                :to="getRouteForLink(item.linkType, item.linkId)"
                class="font-weight-bold"
              >{{ item.contextLabel }}</router-link>
              <span v-else-if="item.contextLabel" class="font-weight-bold">{{ item.contextLabel }}</span>{{' '}}
              <span class="text-muted" v-b-tooltip.hover.html="item.detail && item.detail !== item.summary ? item.detail : ''" :class="{ 'has-detail': item.detail && item.detail !== item.summary }">{{ item.summary }}</span>
            </td>
            <td class="col-meta text-right text-nowrap">
              <template v-if="compact">
                <div v-if="item.actor" class="font-weight-bold">{{ item.actor }}</div>
                <small class="text-muted" v-b-tooltip.hover :title="formatFullDate(item.timestamp)">{{ formatTimestamp(item.timestamp) }}</small>
              </template>
              <template v-else>
                <span v-if="item.actor" class="font-weight-bold">{{ item.actor }}</span>
                <small class="text-muted ml-1" v-b-tooltip.hover :title="formatFullDate(item.timestamp)">{{ formatTimestamp(item.timestamp) }}</small>
              </template>
            </td>
          </tr>
        </template>
      </table>

      <!-- Load More / View All -->
      <div class="text-center mt-1 view-more-wrap">
        <b-button v-if="compact && items.length > compactLimit" variant="primary" size="sm" @click="$emit('view-all')">
          View More Activity
        </b-button>
        <b-button
          v-else-if="!compact && hasMore"
          variant="outline-primary"
          size="sm"
          :disabled="loading"
          @click="loadMore"
        >
          <b-spinner v-if="loading" small class="mr-1" />
          Load More
        </b-button>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { Component, Prop, Vue, Watch } from 'vue-property-decorator';
import { ActivityFeedItem, ActivityFeedResponse } from '@/models/ActivityFeedItem';
import axios, { CancelTokenSource } from 'axios';
import moment from 'moment';

interface ActivityTypeOption {
  value: string;
  label: string;
  icon: string;
}

interface TimeGroup {
  label: string;
  items: ActivityFeedItem[];
}

@Component({
  name: 'PatientActivityFeed',
})
export default class PatientActivityFeed extends Vue {
  @Prop({ required: true }) patientId!: number;
  @Prop({ default: false }) compact!: boolean;

  private items: ActivityFeedItem[] = [];
  private loading = false;
  private error = '';
  private hasMore = false;
  private nextCursor: string | null = null;
  private nextCursorId: string | null = null;
  private compactLimit = 5;
  private cancelTokenSource: CancelTokenSource | null = null;
  private failedProviders: string[] = [];

  private selectedTypes: string[] = [];

  private availableTypes: ActivityTypeOption[] = [
    { value: 'Note', label: 'Note', icon: 'fas fa-sticky-note' },
    { value: 'Order', label: 'Order', icon: 'fas fa-shopping-cart' },
    { value: 'Prescription', label: 'Prescription', icon: 'fas fa-prescription-bottle-alt' },
    { value: 'Case', label: 'Case', icon: 'fas fa-folder-open' },
    { value: 'Shipment', label: 'Shipment', icon: 'fas fa-truck' },
    { value: 'CallLog', label: 'Call', icon: 'fas fa-phone' },
    { value: 'FieldChange', label: 'Change', icon: 'fas fa-history' },
  ];

  private get defaultTypes(): string[] {
    return this.availableTypes.map(t => t.value);
  }

  get displayItems(): ActivityFeedItem[] {
    if (this.compact) {
      return this.items.slice(0, this.compactLimit);
    }
    return this.items;
  }

  get groupedItems(): TimeGroup[] {
    const items = this.displayItems;
    const now = moment();
    const groups: Map<string, ActivityFeedItem[]> = new Map();
    const orderedLabels: string[] = [];

    for (const item of items) {
      const label = this.getTimeGroupLabel(moment.utc(item.timestamp).local(), now);
      if (!groups.has(label)) {
        groups.set(label, []);
        orderedLabels.push(label);
      }
      groups.get(label)!.push(item);
    }

    return orderedLabels.map(label => ({ label, items: groups.get(label)! }));
  }

  getTimeGroupLabel(ts: moment.Moment, now: moment.Moment): string {
    const startOfToday = now.clone().startOf('day');
    if (ts.isSameOrAfter(startOfToday)) return 'Today';
    if (ts.isSameOrAfter(startOfToday.clone().subtract(1, 'day'))) return 'Yesterday';
    if (ts.isSameOrAfter(now.clone().startOf('isoWeek'))) return 'This Week';
    if (ts.isSameOrAfter(now.clone().startOf('isoWeek').subtract(1, 'week'))) return 'Last Week';
    if (ts.isSameOrAfter(now.clone().startOf('month'))) return 'This Month';
    if (ts.isSameOrAfter(now.clone().startOf('month').subtract(1, 'month'))) return 'Last Month';
    return ts.format('MMMM YYYY');
  }

  mounted() {
    this.selectedTypes = [...this.defaultTypes];
    this.loadFeed();
  }

  clearFilters() {
    this.selectedTypes = [...this.defaultTypes];
    this.reload();
  }

  toggleFilterTag(type: string) {
    if (this.selectedTypes.includes(type)) {
      this.selectedTypes = this.selectedTypes.filter(t => t !== type);
    } else {
      this.selectedTypes.push(type);
    }
    this.reload();
  }

  @Watch('patientId')
  onPatientChanged() {
    if (this.patientId) {
      this.reload();
    }
  }

  reload() {
    this.items = [];
    this.nextCursor = null;
    this.nextCursorId = null;
    this.hasMore = false;
    this.failedProviders = [];
    this.loadFeed();
  }

  async loadFeed() {
    if (!this.patientId) return;

    this.cancelTokenSource?.cancel('superseded');
    this.cancelTokenSource = axios.CancelToken.source();

    this.loading = true;
    this.error = '';

    try {
      const params: any = {
        pageSize: this.compact ? this.compactLimit + 1 : 20,
      };

      if (this.nextCursor) {
        params.before = this.nextCursor;
      }
      if (this.nextCursorId) {
        params.beforeActivityId = this.nextCursorId;
      }

      if (this.selectedTypes.length < this.availableTypes.length) {
        params.activityTypes = this.selectedTypes.join(',');
      }

      const response = await axios.get<ActivityFeedResponse>(
        `/api/Patient/${this.patientId}/activity-feed`,
        { params, cancelToken: this.cancelTokenSource.token }
      );

      const data = response.data;
      this.items = [...this.items, ...data.items];
      this.nextCursor = data.nextCursor;
      this.nextCursorId = data.nextCursorId;
      this.hasMore = data.hasMore;
      this.failedProviders = data.failedProviders ?? [];
    } catch (err: any) {
      if (axios.isCancel(err)) return;
      this.error = err.response?.data?.message || err.message || 'Failed to load activity feed';
      console.error('PatientActivityFeed: Error:', err);
    } finally {
      this.loading = false;
    }
  }

  loadMore() {
    this.loadFeed();
  }

  formatTimestamp(ts: string): string {
    return moment.utc(ts).local().fromNow();
  }

  formatFullDate(ts: string): string {
    return moment.utc(ts).local().format('MMMM D, YYYY h:mm A');
  }

  getTypeColor(activityType: string): string {
    const colors: Record<string, string> = {
      Note: '#6c757d',
      Order: '#007bff',
      Prescription: '#28a745',
      Case: '#fd7e14',
      Shipment: '#17a2b8',
      CallLog: '#6f42c1',
      FieldChange: '#ffc107',
    };
    return colors[activityType] || '#6c757d';
  }

  getTypeLabel(activityType: string): string {
    const match = this.availableTypes.find(t => t.value === activityType);
    return match ? match.label : activityType;
  }

  getRouteForLink(linkType: string | null, linkId: string | null): object | null {
    if (!linkType || !linkId) return null;
    switch (linkType) {
      case 'Order':
        return { name: 'OrderDetails', params: { orderId: linkId } };
      case 'Case':
        return { name: 'CaseDetail', params: { id: linkId } };
      case 'UserTask':
        return { name: 'Tasks', params: { taskId: linkId } };
      case 'CallLog':
        return { name: 'CallLog', params: { callId: linkId } };
      case 'Prescription': {
        const parts = linkId.split('/');
        if (parts.length === 3) {
          return { name: 'PrescriptionDetails', params: { storeID: parts[0], rxNumber: parts[1], rfNumber: parts[2] } };
        }
        return null;
      }
      default:
        return null;
    }
  }
}
</script>

<style scoped>
.activity-table {
  width: 100%;
  border-collapse: collapse;
}

.activity-table.edge-to-edge {
  width: calc(100% + 2rem);
  margin-left: -1rem;
  margin-right: -1rem;
}

.activity-row td {
  padding: 4px 6px;
  vertical-align: middle;
  border-bottom: 1px solid #f0f0f0;
}

.activity-row:last-child td {
  border-bottom: none;
}

.col-type {
  width: 1%;
  white-space: nowrap;
  padding-right: 8px !important;
}

.activity-type-label {
  font-weight: 700;
  color: #000;
  font-size: .9rem;
}

.col-type .activity-type-label {
  display: block;
  text-align: left;
}

.col-summary {
  overflow: hidden;
  text-overflow: ellipsis;
}

.col-summary .has-detail {
  border-bottom: 1px dotted #999;
  cursor: help;
}

.col-meta {
  width: 1%;
  white-space: nowrap;
  font-size: .85em;
}

.time-group-header td {
  position: sticky;
  top: 38px;
  z-index: 1;
  border-bottom: 1px solid #dee2e6;
  background: #f8f9fa;
  letter-spacing: 0.05em;
  font-size: .75em;
}

.field-filters {
  gap: 5px;
}

.filter-bar-edge {
  margin-left: -1rem;
  margin-right: -1rem;
  padding-left: 1rem;
  padding-right: 1rem;
}

.view-more-wrap {
  margin-bottom: -0.5rem;
}

.field-filters .badge {
  cursor: pointer;
  font-size: .85em;
}
</style>
